import React, { useEffect, useState } from "react";
import i18n from "../../i18n/config";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ALLOCATION_TYPES } from "../../constants/enums";
import { ROUTES } from "../../constants/clientRoutes";
import { AllocationType, GetFreeTransfersResponse, SelectedAllocationType } from "../../types/api/allocations";
import { setIsInert } from "../../actions/global";
import { ResetAllocationsToSubmit, SetAllocationsToSubmit, SetSelectedAllocations } from "../../actions/allocations";
import AllocationsApi from "../../api/allocations";
import useErrorModal from "../../hooks/useErrorModal";
import { WizardChildProps } from "../../types/props";

const AccountSelection = (props: WizardChildProps) => {
  const { setTitle, setForwardButton } = props;
  const dispatch = useDispatch();
  const allocations = useSelector((state: any) => state.allocations.availableAllocations);
  const [checkedAllocations, setCheckedAllocations] = useState<string[]>([]);
  const history = useHistory();
  const openErrorModal = useErrorModal();
  const initialForwardButtonState = {
    text: i18n.t("COMMON.NEXT"),
    onClick: () => handleNextClick(),
    disabled: true
  };

  /**
   * Sent to page from previous page modal, which sets isInert to true on document body
   * So we need to set it back to false on page load due to issue with history.push being called before modal sets inert back to false
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setIsInert(false));
    dispatch(ResetAllocationsToSubmit());
    setTitle(i18n.t("PAGES.ALLOCATIONS.ACCOUNT_SELECTION.TITLE"));
    setForwardButton(initialForwardButtonState);
  }, []);

  /**
   * If no allocations are checked, disable the next button
   */
  useEffect(() => {
    if (checkedAllocations.length > 0) {
      setForwardButton({ ...initialForwardButtonState, disabled: false });
    } else {
      setForwardButton({ ...initialForwardButtonState, disabled: true });
    }
  }, [checkedAllocations]);

  /**
   * Handles checkbox change
   */
  const handleCheckboxChange = (allocationId: string) => {
    if (checkedAllocations.includes(allocationId)) {
      setCheckedAllocations(checkedAllocations.filter((id) => id !== allocationId));
    } else {
      setCheckedAllocations([...checkedAllocations, allocationId]);
    }
  }

  /**
   * Handles next click
   * If user has selected only a card allocation, send them to the card 100% allocation page
   * Otherwise, get free transfers and send user to the next page
   */
  const handleNextClick = async () => {
    setForwardButton({ ...initialForwardButtonState, disabled: false, loading: true });
    if (checkedAllocations.length === 1) {
      const firstAllocation = getFirstAllocation();

      // if first and only allocation is a card allocation, send user to card 100% allocation page
      if (firstAllocation.cardNumberLastFour) {
        const mutableFirstAllocation = { ...firstAllocation };
        // send user to card 100% allocation page
        const mappedAllocations = mapFreeTransferAllocations([mutableFirstAllocation], true);
        dispatch(SetAllocationsToSubmit(mappedAllocations));
        history.push(ROUTES.ALLOCATIONS.CONFIRMATION, { type: ALLOCATION_TYPES.ALL_TO_CARD });
      } else {
        getFreeTransfers();
      }
    } else {
      getFreeTransfers();
    }
  }

  const getFreeTransfers = async () => {
    try {
      const cardAllocation = allocations.find((allocation: AllocationType) => allocation.cardNumberLastFour);
      const requestBody = checkedAllocations.filter((id: string) => id !== String(cardAllocation?.id));

      const response = await AllocationsApi.GetFreeTransfers(requestBody) as GetFreeTransfersResponse;
      let selectedAllocations: SelectedAllocationType[] = [];

      if (checkedAllocations.find((id: string) => id === String(cardAllocation?.id))) {
        const cardAllocationObj = createSelectedAllocationObjForCard(cardAllocation);
        selectedAllocations = [cardAllocationObj, ...response.accounts];
      } else {
        selectedAllocations = response.accounts;
      }

      const mappedAllocations = mapFreeTransferAllocations(selectedAllocations);
      dispatch(SetSelectedAllocations(mappedAllocations, response.feePeriod));
      history.push(ROUTES.ALLOCATIONS.SELECT_FREE_TRANSFER);

    } catch (err) {
      setForwardButton({ ...initialForwardButtonState, loading: false });
      openErrorModal(err);
    }
  }

  /**
   * Gets first selected allocation (to eventually check if is card allocation)
   * @returns boolean
   */
  const getFirstAllocation = () => {
    return allocations.find((allocation: AllocationType) => allocation.id === Number(checkedAllocations[0]));
  }

  const createSelectedAllocationObjForCard = (allocation: AllocationType): SelectedAllocationType => {
    return {
      id: allocation.id,
      accountName: allocation.accountName,
      bankName: allocation.name,
      accountNumber: allocation.accountNumber,
      currencyId: allocation.destinationCurrency.id,
      feeAmountString: "",
      selected: false,
      fixed: allocation.fixed,
      percentage: allocation.percentage,
      getsRemainder: allocation.getsRemainder,
    }
  }

  const mapFreeTransferAllocations = (selectedAllocations: SelectedAllocationType[], isAllToCard = false) => {
    const availableAllocations = allocations.map((allocation: AllocationType) => ({ ...allocation }));

    return selectedAllocations.map((allocation) => {
      const fullAllocation = availableAllocations.find((a: AllocationType) => a.id === allocation.id);
      if (isAllToCard) {
        fullAllocation.selected = true;
        fullAllocation.percentage = 100;
      } else {
        fullAllocation.percentage = 0;
        fullAllocation.fixed = 0;
      }
      fullAllocation.feeAmountString = allocation.feeAmountString;
      fullAllocation.selected = allocation.selected;
      fullAllocation.showSelect = allocation.showSelect;
      fullAllocation.ruleNumberPaid = allocation.ruleNumberPaid;
      return fullAllocation;
    });
  }

  return (
    <>
      <p>{i18n.t("PAGES.ALLOCATIONS.ACCOUNT_SELECTION.SUBTITLE")}</p>
      <p className="bold spacing-bottom-medium">{i18n.t("PAGES.ALLOCATIONS.ACCOUNT_SELECTION.CHECKBOX_HEADING")}</p>

      {allocations.map((allocation: AllocationType) => (
        <div className="checkbox spacing-bottom-medium" key={allocation.id}>
          <input
            type="checkbox"
            name={String(allocation.id)}
            id={allocation.accountName}
            onChange={(e) => handleCheckboxChange(e.target.name)}
          />{" "}
          <label htmlFor={allocation.accountName}>
            <span className="bold navy-blue">{allocation.accountName}</span> <br />
            <span className="italic">{allocation.name}</span>
          </label>
        </div>
      ))}
    </>
  );
}

export default AccountSelection;